import { useState, type FormEvent } from 'react';
import SectionHeading from '@/components/base/SectionHeading';
import { siteInfo } from '@/mocks/home';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/daim83joh653ivfvog50';
const FORM_ID = 'form-daim83joh653ivfvog50';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const principles = [
  { icon: 'ri-focus-3-line', title: '定位', desc: '只做一件事——把吃瓜这件事讲清楚，做新手可以照着走的攻略与教程。' },
  { icon: 'ri-heart-3-line', title: '理念', desc: '信息可以有热度，表达要有温度。理性围观，不煽动、不带节奏。' },
  { icon: 'ri-shield-check-line', title: '原则', desc: '不传播未经证实的内容，不虚构来源，尊重当事人的隐私与感受。' },
];

export default function AboutSection() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      const text = String(value).trim();
      if (text) params.append(key, text);
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const looksSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK';

      if (isOk && !looksSpam) {
        setStatus('success');
        form.reset();
      } else {
        setFormError(serverMsg || '提交失败，请稍后重试。');
        setStatus('error');
      }
    } catch {
      setFormError('网络异常，提交失败，请稍后重试。');
      setStatus('error');
    }
  };

  return (
    <section id="about" className="w-full bg-background-100 py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1fr] lg:gap-14">
          <div>
            <SectionHeading
              eyebrow="关于我们"
              title={`关于 ${siteInfo.name}`}
              desc={siteInfo.intro}
            />

            <div className="mt-8 flex flex-col gap-3">
              {principles.map((item) => (
                <div key={item.title} className="flex items-start gap-3.5 rounded-lg border border-background-200/80 bg-background-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className={`${item.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-heading text-sm font-bold text-foreground-950 md:text-base">{item.title}</h3>
                    <p className="mt-1 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-lg bg-primary-900 p-6">
              <p className="font-heading text-base font-bold text-background-50">{siteInfo.slogan}</p>
              <div className="mt-4 flex flex-col gap-2.5 sm:flex-row sm:items-center sm:gap-6">
                <a
                  href={`mailto:${siteInfo.email}`}
                  className="inline-flex items-center gap-2 whitespace-nowrap font-label text-sm text-background-100 transition-colors hover:text-primary-300"
                >
                  <i className="ri-mail-line text-base" aria-hidden="true"></i>
                  {siteInfo.email}
                </a>
                <span className="inline-flex items-center gap-2 font-label text-sm text-background-100">
                  <i className="ri-time-line text-base" aria-hidden="true"></i>
                  工作日 10:00 – 19:00 回复
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-background-200/80 bg-background-50 p-6 md:p-8">
            <h3 className="font-heading text-lg font-bold text-foreground-950">留言咨询</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">
              对攻略有疑问，或者想推荐一个值得跟进的话题？填好下面的信息，我们会尽快回复。
            </p>

            {status === 'success' ? (
              <div className="mt-6 rounded-lg border border-accent-300/70 bg-accent-50 p-6 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-500 text-background-50">
                  <i className="ri-check-line text-2xl" aria-hidden="true"></i>
                </span>
                <p className="mt-4 font-heading text-base font-bold text-foreground-950">留言已提交，感谢反馈</p>
                <p className="mt-1.5 text-sm text-foreground-600">我们会认真阅读每一条建议，并持续更新这份吃瓜攻略。</p>
                <button
                  type="button"
                  onClick={() => setStatus('idle')}
                  className="mt-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-4 py-2 font-label text-sm font-medium text-accent-800 transition-colors hover:bg-accent-100"
                >
                  <i className="ri-edit-line text-base" aria-hidden="true"></i>
                  再写一条
                </button>
              </div>
            ) : (
              <form
                id={FORM_ID}
                data-readdy-form
                onSubmit={handleSubmit}
                className="mt-6 flex flex-col gap-4"
                noValidate
              >
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-name" className="font-label text-sm font-medium text-foreground-800">
                    你的称呼
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="例如：林小满"
                    className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-email" className="font-label text-sm font-medium text-foreground-800">
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-topic" className="font-label text-sm font-medium text-foreground-800">
                    咨询类型
                  </label>
                  <select
                    id="contact-topic"
                    name="topic"
                    defaultValue="攻略疑问"
                    className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="攻略疑问">攻略疑问</option>
                    <option value="话题推荐">话题推荐</option>
                    <option value="内容纠错">内容纠错</option>
                    <option value="合作交流">合作交流</option>
                  </select>
                </div>

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="contact-message" className="font-label text-sm font-medium text-foreground-800">
                    留言内容
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={4}
                    maxLength={500}
                    placeholder="把你的疑问或建议写在这里（最多 500 字）"
                    className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="field-extra-note"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === 'error' && formError ? (
                  <p className="flex items-start gap-2 rounded-md border border-primary-300/70 bg-primary-50 px-3.5 py-2.5 text-sm text-primary-800">
                    <i className="ri-error-warning-line mt-0.5 text-base" aria-hidden="true"></i>
                    {formError}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-1 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin text-base' : 'ri-send-plane-line text-base'} aria-hidden="true"></i>
                  {status === 'submitting' ? '正在提交…' : '提交留言'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}